import Input from 'components/Input';
import Select from 'components/Select';
import { useFormik } from 'formik';
import { useMemoTranslations } from 'hooks/Intl';
import * as translations from 'intls/medicine';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import useDebounce from 'hooks/useDebounce';
import { medicineService } from 'services';
import { useMedicine } from 'hooks/Medicine';

interface MedicineSearchProps {
  fetchStatusState?: [LoadingStatus, Dispatch<SetStateAction<LoadingStatus>>];
  medicinesState: [Medicine[], Dispatch<SetStateAction<Medicine[]>>];
}

const MedicineSearch = ({
  fetchStatusState,
  medicinesState,
}: MedicineSearchProps) => {
  const [, setFetchStatus] = fetchStatusState || [];
  const [, setMedicines] = medicinesState;

  const intl = useMemoTranslations(translations);

  const { countryOptions } = useMedicine();

  const [isFirstSearchDone, setIsFirstSearchDone] = useState(false);

  const formik = useFormik({
    initialValues: { query: '', country: '' },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const fetchMedicines = async (country: string, query: string) => {
    try {
      setFetchStatus?.('LOADING');
      const { data: medicines } = await medicineService.search({
        q: query,
        country,
      });
      setMedicines(medicines);
      setFetchStatus?.('LOADED');
    } catch (error) {
      console.log(error);
      setFetchStatus?.('ERROR');
    }
  };

  const fetchMedicinesDebounced = useDebounce(
    () => fetchMedicines(formik.values.country, formik.values.query),
    1000
  );

  useEffect(() => {
    const { country, query } = formik.values;
    if (!country || query.length < 5) return;

    if (isFirstSearchDone) fetchMedicinesDebounced();
    else {
      setIsFirstSearchDone(true);
      fetchMedicines(country, query);
    }
  }, [formik.values.query, formik.values.country]);

  return (
    <div className="row">
      <Input
        label={intl.MEDICINE}
        formikProps={formik}
        name="query"
        maxLength={55}
        className="col-12 col-sm-8"
      />
      <Select
        label={intl.MEDICINE_COUNTRY_OF_ORIGIN}
        formikProps={formik}
        name="country"
        className="col-12 col-sm-4"
      >
        <option value="">{intl.MEDICINE_COUNTRY_OF_ORIGIN}</option>
        {countryOptions.map(option => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
};

export default MedicineSearch;
